import React, { useState } from 'react';
import {
	ClipboardDocumentIcon,
	TrashIcon,
	PlusIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';
import Button from '../../../components/Button';
import DataTable from '../../../components/DataTable';
import Modal from '../../../components/Shell/Modal';
import { TemplateField } from '../../../components/Attributes/Attributes_old';

import {
	getApiTokens,
	createApiToken,
	revokeApiToken,
} from '../../../api/company';

const ApiTokens = () => {
	const [isCreateModalOpen, setCreateModalOpen] = useState(false);
	const [newToken, setNewToken] = useState(null);
	const [form, setForm] = useState({ name: '', scopes: [] });

	const { setOpen: openDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');

	const {
		value: tokens,
		execute: refreshTokens,
		status: tokensStatus,
	} = useAsync(getApiTokens);

	const { execute: onCreate } = useAsync(createApiToken, {
		immediate: false,
		showNotification: { error: 'raw' },
	});

	const { execute: onRevoke } = useAsync(revokeApiToken, {
		immediate: false,
		showNotification: { success: 'Token revoked', error: 'raw' },
	});

	const handleChange = (name, value) => {
		setForm((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleScopeToggle = (scopeValue) => {
		setForm((prev) => {
			const currentScopes = prev.scopes || [];
			if (currentScopes.includes(scopeValue)) {
				return {
					...prev,
					scopes: currentScopes.filter((s) => s !== scopeValue),
				};
			} else {
				return { ...prev, scopes: [...currentScopes, scopeValue] };
			}
		});
	};

	const handleSelectAllScopes = () => {
		const allScopeValues = scopeOptions.map((s) => s.value);
		const allSelected = allScopeValues.every((s) => form.scopes?.includes(s));
		if (allSelected) {
			setForm((prev) => ({ ...prev, scopes: [] }));
		} else {
			setForm((prev) => ({ ...prev, scopes: allScopeValues }));
		}
	};

	const handleCreateToken = () => {
		onCreate(form, (response) => {
			if (response?.data?.token) {
				setNewToken(response.data);
				setForm({ name: '', scopes: [] });
				setCreateModalOpen(false);
				refreshTokens();
			}
		});
	};

	const handleCopyToken = (token) => {
		navigator.clipboard.writeText(token);
		toast.success('Token copied to clipboard');
	};

	const handleRevokeToken = (tokenId, tokenName) => {
		openDeleteModal({
			onConfirm: () => {
				onRevoke({ tokenId }, () => {
					closeDeleteModal();
					refreshTokens();
				});
			},
			confirmText: 'Revoke Token',
			title: `Revoke API Token "${tokenName}"?`,
		});
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'Never';
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	const scopeOptions = [
		{ label: 'Assets: Read', value: 'assets:read' },
		{ label: 'Assets: Write', value: 'assets:write' },
		{ label: 'Reservations: Read', value: 'reservations:read' },
		{ label: 'Reservations: Write', value: 'reservations:write' },
	];

	const columns = [
		{
			header: 'NAME',
			accessorKey: 'name',
			cell: (info) => (
				<div className="font-medium text-gray-900">{info.getValue()}</div>
			),
		},
		{
			header: 'TOKEN PREFIX',
			accessorKey: 'tokenPrefix',
			cell: (info) => (
				<code className="text-xs bg-gray-100 px-2 py-1 rounded">
					{info.getValue()}...
				</code>
			),
		},
		{
			header: 'SCOPES',
			accessorKey: 'scopes',
			cell: (info) => (
				<div className="flex flex-wrap gap-1">
					{info.getValue()?.map((scope) => (
						<span
							key={scope}
							className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded"
						>
							{scope}
						</span>
					))}
				</div>
			),
		},
		{
			header: 'LAST USED',
			accessorKey: 'lastUsedAt',
			cell: (info) => (
				<span className="text-gray-500">{formatDate(info.getValue())}</span>
			),
		},
		{
			header: 'CREATED',
			accessorKey: 'createdAt',
			cell: (info) => (
				<span className="text-gray-500">{formatDate(info.getValue())}</span>
			),
		},
		{
			header: '',
			accessorKey: 'id',
			cell: (info) => (
				<button
					onClick={() =>
						handleRevokeToken(info.getValue(), info.row.original.name)
					}
					className="text-red-600 hover:text-red-800"
				>
					<TrashIcon className="h-4 w-4" />
				</button>
			),
		},
	];

	return (
		<>
			<div>
				<div className="flex justify-between items-start mb-6">
					<div>
						<h2 className="text-base font-semibold leading-6 text-gray-900">
							API Tokens
						</h2>
						<p className="mt-1 text-sm text-gray-500">
							Create and manage API tokens for third-party integrations. Tokens
							provide access to your organization's data via the public API.
						</p>
					</div>
					<Button onClick={() => setCreateModalOpen(true)}>
						<PlusIcon className="h-4 w-4 mr-1" />
						Create Token
					</Button>
				</div>

				<DataTable
					columns={columns}
					data={{ rows: tokens?.data || [] }}
					hasCheckboxes={false}
					manualSorting={false}
					usePagination={false}
					useSearch={false}
					useExport={false}
					isLoading={tokensStatus === 'pending'}
					emptyMessage="No API tokens created yet"
				/>
			</div>

			{/* Create Token Modal */}
			<Modal
				title="Create API Token"
				open={isCreateModalOpen}
				onClose={() => {
					setCreateModalOpen(false);
					setForm({ name: '', scopes: [] });
				}}
			>
				<div className="space-y-4">
					<TemplateField
						name="Token Name"
						dataIndex="name"
						value={form.name}
						onChange={handleChange}
						placeholder="e.g., Production Integration"
						autoFocus
					/>

					<div>
						<label className="block text-xs text-gray-400 mb-2">Scopes</label>
						<div className="space-y-2">
							<label className="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									checked={scopeOptions.every((s) =>
										form.scopes?.includes(s.value),
									)}
									onChange={handleSelectAllScopes}
									className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
								/>
								<span className="text-sm font-medium text-gray-700">
									Select All
								</span>
							</label>
							<div className="border-t border-gray-200 pt-2 space-y-2">
								{scopeOptions.map((scope) => (
									<label
										key={scope.value}
										className="flex items-center gap-2 cursor-pointer"
									>
										<input
											type="checkbox"
											checked={form.scopes?.includes(scope.value) || false}
											onChange={() => handleScopeToggle(scope.value)}
											className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
										/>
										<span className="text-sm text-gray-600">{scope.label}</span>
									</label>
								))}
							</div>
						</div>
						<p className="mt-2 text-xs text-gray-400">
							Select the permissions for this token
						</p>
					</div>

					<div className="flex justify-end pt-4">
						<Button
							type="secondary"
							onClick={() => {
								setCreateModalOpen(false);
								setForm({ name: '', scopes: [] });
							}}
							className="mr-2"
						>
							Cancel
						</Button>
						<Button
							disabled={!form.name || !form.scopes?.length}
							onClick={handleCreateToken}
						>
							Create Token
						</Button>
					</div>
				</div>
			</Modal>

			{/* New Token Display Modal */}
			<Modal
				title="Token Created"
				open={!!newToken}
				onClose={() => setNewToken(null)}
				preventBackdropClose
			>
				<div className="space-y-4">
					<div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
						<p className="text-sm text-yellow-800">
							Make sure to copy your token now. You won't be able to see it
							again!
						</p>
					</div>

					<div>
						<label className="block text-xs text-gray-400 mb-1">
							Your API Token
						</label>
						<div className="flex items-center gap-2">
							<code className="flex-1 text-sm bg-gray-100 px-3 py-2 rounded border font-mono break-all">
								{newToken?.token}
							</code>
							<button
								onClick={() => handleCopyToken(newToken?.token)}
								className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
								title="Copy to clipboard"
							>
								<ClipboardDocumentIcon className="h-5 w-5" />
							</button>
						</div>
					</div>

					<div className="flex justify-end pt-4">
						<Button onClick={() => setNewToken(null)}>Done</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default ApiTokens;
